<script setup name="NextPlayInfo">
import { inject, computed, watch, ref, onMounted } from 'vue'
import dayjs from 'dayjs'

const size = inject('size')
const currentVideo = inject('currentVideo', {})
const nextVideo = inject('nextVideo', {})

const hasNext = computed(() => {
  return nextVideo?.value?.id
})

const image = ref('')
const title = ref('')
const categoryName = ref('')
const updatedAt = ref('')

const setNextInfo = () => {
  image.value = nextVideo?.value?.image_info?.small || currentVideo?.value?.image_info?.small
  title.value = nextVideo?.value?.title || currentVideo?.value?.title
  categoryName.value = nextVideo?.value?.category_name || currentVideo?.value?.category_name
  updatedAt.value = nextVideo?.value?.updated_at || currentVideo?.value?.updated_at
}

onMounted(setNextInfo)

watch([nextVideo, currentVideo], setNextInfo, { deep: true })
</script>

<template>
  <div class="tvb-next-play-info" :class="[size]">
    <div class="tvb-next-play-info-container">
      <div class="tvb-next-play-info-image">
        <tvb-image
          class="tvb-next-play-info-image-el"
          v-if="image"
          :src="image"
          :alt="title"
          :key="image"
        />
        <p class="tvb-next-play-info-status-text">{{ hasNext ? '即将播放' : '當前播放' }}</p>
      </div>
      <div class="tvb-next-play-info-detail">
        <p class="tvb-next-play-info-title">{{ title }}</p>
        <div class="tvb-next-play-info-other">
          <p class="tvb-next-play-info-category">{{ categoryName }}</p>
          <p class="tvb-next-play-info-updated_at" v-if="updatedAt">
            {{ dayjs.unix(updatedAt).format('YYYY-MM-DD HH:mm') }}
          </p>
        </div>
      </div>
      <div class="tvb-next-play-info-play-list">
        <tvb-svg-icon class="tvb-next-play-info-play-list-icon" name="play_list" />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.tvb-next-play-info {
  margin-top: 16px;
  display: flex;
  align-items: center;
  padding: 8px;
  background: #f5f6f7;
  border-radius: 24px;
  backdrop-filter: blur(25px);
  cursor: pointer;
  .tvb-next-play-info-container {
    width: 100%;
    padding: 8px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    gap: 10px;
    background: #fff;
    // width: calc(100% - 16px);
  }

  &.l,
  &.m {
    @media (max-width: 1143px) {
      width: 100%;
    }
  }

  &.s,
  &.xs {
    padding: 7px;
    background: rgba(236, 239, 243, 0.55);
    // border: 7px solid rgba(236, 239, 243, 0.55);
    box-shadow:
      0 0 6px 0 rgba(0, 0, 0, 0.12),
      1px 1px 0 -0.5px #fff inset,
      -1px -1px 0 -0.5px #fff inset,
      1px 1px 0.5px -1px #fff inset,
      -1px -1px 0.5px -1px #fff inset,
      0 0 3px 0 rgba(255, 255, 255, 0.5) inset,
      0 0 11px 0 #f2f2f2 inset;

    // gap: 12px;

    .tvb-next-play-info-container {
      padding: 7px;
      // TC-1482：image 与 detail 中间间距 4px（设计稿，覆盖桌面默认 10px）
      gap: 4px;
    }

    .tvb-next-play-info-image {
      width: 78px;
      min-width: 78px;
      height: 44px;

      .tvb-next-play-info-status-text {
        font-size: 12px;
        line-height: 18px;
      }
    }

    .tvb-next-play-info-detail {
      width: calc(100% - 140px);
      gap: 2px;

      .tvb-next-play-info-title {
        font-size: 14px;
        line-height: 20px;
      }

      .tvb-next-play-info-other {
        .tvb-next-play-info-category,
        .tvb-next-play-info-updated_at {
          font-size: 12px;
          line-height: 18px;
        }
      }
    }

    .tvb-next-play-info-play-list {
      padding: 0 7px 0 14px;
    }
  }

  .tvb-next-play-info-image {
    position: relative;
    width: 89px;
    min-width: 89px;
    height: 50px;
    border-radius: 10px;
    overflow: hidden;

    .tvb-next-play-info-status-text {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.4);
      border-radius: inherit;
      display: flex;
      align-items: center;
      justify-content: center;
      color: rgba(255, 255, 255, 0.95);
      font-size: 12px;
      font-weight: 400;
      line-height: 18px;
    }
  }

  .tvb-next-play-info-detail {
    width: calc(100% - 160px);
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 4px;

    .tvb-next-play-info-title {
      width: 100%;
      overflow: hidden;
      color: #231f20;
      text-overflow: ellipsis;
      font-size: 16px;
      font-weight: 400;
      line-height: 24px;
      letter-spacing: 0.5px;
      white-space: nowrap;
    }

    .tvb-next-play-info-other {
      display: flex;
      align-items: center;
      gap: 10px;

      .tvb-next-play-info-category,
      .tvb-next-play-info-updated_at {
        color: #818d95;
        font-size: 14px;
        font-weight: 400;
        line-height: 20px;
        letter-spacing: 0.3px;
        white-space: nowrap;
      }
    }
  }

  .tvb-next-play-info-play-list {
    height: 32px;
    position: relative;
    padding: 0 8px 0 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: auto;

    &::after {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      width: 1px;
      height: 32px;
      background: #d9dddf;
      border-radius: inherit;
    }

    .tvb-next-play-info-play-list-icon {
      font-size: 24px;
    }
  }
}
</style>
